import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Printer, X, RefreshCw, CreditCard, ArrowUp, ArrowDown, Tag, ArrowUpDown, Camera } from 'lucide-react';
import ItemPhotosModal from './ItemPhotosModal';

const BACKEND = () => (window.ENV?.REACT_APP_BACKEND_URL || window.location.origin);

const STATUS_OPTIONS = [
  '', 'TO BE SELECTED', 'RESEARCHING', 'PENDING APPROVAL',
  'APPROVED', 'ORDERED', 'PICKED', 'CONFIRMED',
  'IN PRODUCTION', 'SHIPPED', 'IN TRANSIT', 'OUT FOR DELIVERY',
  'DELIVERED TO RECEIVER', 'DELIVERED TO JOB SITE', 'RECEIVED',
  'READY FOR INSTALL', 'INSTALLING', 'INSTALLED',
  'ON HOLD', 'BACKORDERED', 'DAMAGED', 'RETURNED', 'CANCELLED',
];

const SHIP_TO_OPTIONS = ['', 'JOB SITE', 'RECEIVER', 'WAREHOUSE', 'CLIENT', 'DESIGN STUDIO', 'OTHER'];

// Statuses that mean the money is committed/spent (goods moving or arrived).
// A line in any of these is SUBTRACTED from the outstanding balance.
const FULFILLED_STATUSES = new Set([
  'SHIPPED', 'IN TRANSIT', 'OUT FOR DELIVERY',
  'DELIVERED TO RECEIVER', 'DELIVERED TO JOB SITE', 'RECEIVED',
  'PARTIALLY DELIVERED', 'READY FOR INSTALL', 'INSTALLING', 'INSTALLED',
]);

const UNASSIGNED = '— Unassigned —';

// Auto-loaded row thumbnails (item photo + finish swatch), no click needed.
const PT_THUMB = { width: 46, height: 46, objectFit: 'cover', border: '1px solid #b7ad97', borderRadius: 4, background: '#f0ede4' };

const money = (v) => {
  const n = parseFloat(v || 0);
  return n ? `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '';
};

const titleCase = (s) => (s || '').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());

const statusColor = (s) => {
  const t = (s || '').toLowerCase();
  if (['delivered', 'received', 'installed', 'complete'].some((k) => t.includes(k))) return '#2E6B50';
  if (['transit', 'shipped', 'out for delivery'].some((k) => t.includes(k))) return '#2B4C7E';
  if (['hold', 'backorder', 'delay', 'discontinued', 'damaged', 'returned', 'cancelled'].some((k) => t.includes(k))) return '#8A3A3A';
  if (['ordered', 'confirmed', 'production'].some((k) => t.includes(k))) return '#8b6b2e';
  return '#1a1a1a';
};

const goods = (r) => (parseFloat(r.cost || 0) * parseInt(r.quantity || 1, 10)) || 0;
const isFulfilled = (r) => FULFILLED_STATUSES.has((r.status || '').toString().trim().toUpperCase());

const Cell = ({ value, onSave, type = 'text', style, placeholder, testId }) => {
  const [v, setV] = useState(value ?? '');
  useEffect(() => { setV(value ?? ''); }, [value]);
  return (
    <input
      data-testid={testId || 'pt-cell-input'}
      type={type}
      className="pt-cell-input"
      style={style}
      value={v}
      placeholder={placeholder || ''}
      onChange={(e) => setV(e.target.value)}
      onBlur={() => { if (String(v) !== String(value ?? '')) onSave(v); }}
      onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
    />
  );
};

// Column definitions drive both the header (sortable) and the sort dropdown.
const COLUMNS = [
  { key: 'name', label: 'Item', sort: 'name' },
  { key: 'room', label: 'Room', sort: 'room_name' },
  { key: 'vendor', label: 'Vendor / SKU', sort: 'vendor' },
  { key: 'credit_card', label: 'Credit Card', sort: 'credit_card', nowrap: true },
  { key: 'quantity', label: 'Qty', sort: 'quantity', num: true },
  { key: 'cost', label: 'Unit', sort: 'cost', num: true },
  { key: 'shipping_cost', label: 'Shipping (per order)', sort: 'shipping_cost', num: true },
  { key: 'lineTotal', label: 'Line Total', sort: 'lineTotal', num: true },
  { key: 'order_number', label: 'Order #', sort: 'order_number' },
  { key: 'order_date', label: 'Ordered', sort: 'order_date' },
  { key: 'ship_date', label: 'Ship Date', sort: 'ship_date' },
  { key: 'expected_delivery', label: 'Expected', sort: 'expected_delivery' },
  { key: 'actual_delivery', label: 'Delivered', sort: 'actual_delivery' },
  { key: 'carrier', label: 'Carrier / Tracking', sort: 'carrier' },
  { key: 'ship_to', label: 'Ship To', sort: 'ship_to' },
  { key: 'status', label: 'Status', sort: 'status' },
];

const PurchaseTrackerModal = ({ projectId, onClose }) => {
  const [rows, setRows] = useState(null);
  const [meta, setMeta] = useState({ project_name: '', client_name: '' });
  const [carriers, setCarriers] = useState([]);
  const [cards, setCards] = useState([]);
  const [orderShipping, setOrderShipping] = useState({});
  const [saveState, setSaveState] = useState('');
  const [sortKey, setSortKey] = useState('order_number');
  const [sortDir, setSortDir] = useState('asc');
  const [showCards, setShowCards] = useState(false);
  const [newCard, setNewCard] = useState('');
  const [photoRow, setPhotoRow] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND()}/api/export-sheets/${projectId}/purchase-tracker`);
      const sheet = await res.json();
      const flat = [];
      (sheet.floors || []).forEach((floor) =>
        (floor.rooms || []).forEach((room) =>
          (room.categories || []).forEach((cat) =>
            (cat.items || []).forEach((it) => {
              if (!it.included) return;
              flat.push({ ...it, room_name: room.room_name });
            })
          )
        )
      );
      setMeta({ project_name: sheet.project_name || '', client_name: sheet.client_name || '' });
      setCards(sheet.payment_cards || []);
      setOrderShipping(sheet.order_shipping || {});
      setRows(flat);
    } catch (e) {
      console.error('Purchase tracker load failed', e);
      setRows([]);
    }
  }, [projectId]);

  useEffect(() => {
    load();
    fetch(`${BACKEND()}/api/carrier-options`)
      .then((r) => r.json())
      .then((d) => setCarriers(((d && d.data) || []).map((c) => c.name)))
      .catch(() => setCarriers(['FedEx', 'UPS', 'USPS', 'OTHER']));
  }, [load]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = ''; };
  }, [onClose]);

  const flash = (ok) => setSaveState(ok ? 'saved' : 'error');

  const saveField = async (itemId, field, value) => {
    setRows((prev) => (prev || []).map((r) => (r.item_id === itemId ? { ...r, [field]: value } : r)));
    setSaveState('saving');
    try {
      const res = await fetch(`${BACKEND()}/api/items/${itemId}/quick-update`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ [field]: value }),
      });
      flash(res.ok);
    } catch { flash(false); }
  };

  const putProject = async (patch) => {
    setSaveState('saving');
    try {
      const res = await fetch(`${BACKEND()}/api/projects/${projectId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      });
      flash(res.ok);
    } catch { flash(false); }
  };

  // Shipping is per ORDER, not per item.
  const saveOrderShipping = (orderNumber, value) => {
    const next = { ...orderShipping, [orderNumber]: value };
    setOrderShipping(next);
    putProject({ order_shipping: next });
  };

  const persistCards = (next) => { setCards(next); putProject({ payment_cards: next }); };
  const addCard = () => {
    const c = newCard.trim();
    if (!c || cards.includes(c)) { setNewCard(''); return; }
    persistCards([...cards, c]);
    setNewCard('');
  };
  const removeCard = (c) => persistCards((cards || []).filter((x) => x !== c));

  // Tag a line into the Master To-Do list (and highlight it here).
  const toggleTag = async (r) => {
    if (r.tagged) {
      setRows((prev) => (prev || []).map((x) => (x.item_id === r.item_id ? { ...x, tagged: false, task_todo_id: '' } : x)));
      setSaveState('saving');
      try {
        if (r.task_todo_id) await fetch(`${BACKEND()}/api/todos/${r.task_todo_id}`, { method: 'DELETE' });
        const res = await fetch(`${BACKEND()}/api/items/${r.item_id}/quick-update`, {
          method: 'PATCH', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tagged: false, task_todo_id: '' }),
        });
        flash(res.ok);
      } catch { flash(false); }
      return;
    }
    setSaveState('saving');
    try {
      const todoRes = await fetch(`${BACKEND()}/api/todos`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          project_id: projectId,
          text: `Purchase: ${r.name || 'item'}${r.order_number ? ` (Order ${r.order_number})` : ''}`,
          priority: 'medium',
          status: 'pending',
          source_type: 'ffe',
          linked_ffe_item: {
            item_id: r.item_id, name: r.name || '', room_name: r.room_name || '',
            vendor: r.vendor || '', sku: r.sku || '', category_name: r.category_name || '',
          },
        }),
      });
      const todoData = await todoRes.json();
      const todoId = (todoData && todoData.todo && todoData.todo.id) || '';
      setRows((prev) => (prev || []).map((x) => (x.item_id === r.item_id ? { ...x, tagged: true, task_todo_id: todoId } : x)));
      const res = await fetch(`${BACKEND()}/api/items/${r.item_id}/quick-update`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tagged: true, task_todo_id: todoId }),
      });
      flash(res.ok);
    } catch { flash(false); }
  };

  const openPrint = () => window.open(`${BACKEND()}/api/export-sheets/${projectId}/purchase-tracker/print`, '_blank');

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('asc'); }
  };

  // First row of each order = the "anchor" that carries the order's shipping in totals.
  const orderAnchor = useMemo(() => {
    const m = {};
    (rows || []).forEach((r) => {
      const o = (r.order_number || '').trim();
      if (o && !(o in m)) m[o] = r.item_id;
    });
    return m;
  }, [rows]);

  const shipDisplay = (r) => {
    const o = (r.order_number || '').trim();
    if (o) return orderShipping[o] ?? '';
    return r.shipping_cost ?? '';
  };
  const shipAttributed = (r) => {
    const o = (r.order_number || '').trim();
    if (o) return orderAnchor[o] === r.item_id ? (parseFloat(orderShipping[o] || 0) || 0) : 0;
    return parseFloat(r.shipping_cost || 0) || 0;
  };
  const effective = (r) => goods(r) + shipAttributed(r);

  const sortValue = (r, key) => {
    if (key === 'lineTotal') return goods(r);
    if (key === 'shipping_cost') return parseFloat(shipDisplay(r) || 0) || 0;
    if (key === 'quantity' || key === 'cost') return parseFloat(r[key] || 0) || 0;
    return (r[key] ?? '').toString().trim().toLowerCase();
  };

  const sortedRows = useMemo(() => {
    const arr = [...(rows || [])];
    arr.sort((a, b) => {
      const av = sortValue(a, sortKey);
      const bv = sortValue(b, sortKey);
      let cmp;
      if (typeof av === 'number' && typeof bv === 'number') cmp = av - bv;
      else cmp = String(av).localeCompare(String(bv), undefined, { numeric: true });
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return arr;
    // eslint-disable-next-line
  }, [rows, sortKey, sortDir, orderShipping]);

  const fullOrder = (rows || []).reduce((s, r) => s + effective(r), 0);
  const fulfilledTotal = (rows || []).filter(isFulfilled).reduce((s, r) => s + effective(r), 0);
  const outstanding = fullOrder - fulfilledTotal;

  const perCard = useMemo(() => {
    const m = {};
    (rows || []).forEach((r) => {
      const c = (r.credit_card || '').trim() || UNASSIGNED;
      m[c] = (m[c] || 0) + effective(r);
    });
    return Object.entries(m).sort((a, b) => b[1] - a[1]);
    // eslint-disable-next-line
  }, [rows, orderShipping]);

  const th = { background: '#6b5745', color: '#fff', fontSize: 11, letterSpacing: '.04em', textTransform: 'uppercase', padding: '9px 8px', textAlign: 'left', whiteSpace: 'nowrap', position: 'sticky', top: 0, zIndex: 2, cursor: 'pointer', userSelect: 'none', lineHeight: 1.2 };
  const td = { borderBottom: '1px solid #e4e1d9', padding: '6px 7px', verticalAlign: 'top' };

  const subtitle = meta.client_name ? `${meta.project_name} - ${meta.client_name}` : meta.project_name;

  const SortHead = ({ col }) => (
    <th
      style={{ ...th, textAlign: col.num ? 'right' : 'left', width: col.nowrap ? '1%' : undefined }}
      onClick={() => toggleSort(col.sort)}
      data-testid={`pt-sort-${col.sort}`}
      title={`Sort by ${col.label}`}
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, justifyContent: col.num ? 'flex-end' : 'flex-start' }}>
        {col.label}
        {sortKey === col.sort ? (sortDir === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />) : null}
      </span>
    </th>
  );

  return createPortal(
    <div data-testid="purchase-tracker-modal" style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(3px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12 }}>
      <style>{`
        [data-testid='purchase-tracker-modal'] input, [data-testid='purchase-tracker-modal'] select {
          background: transparent !important; color: #1a1a1a !important; color-scheme: light;
        }
        .pt-cell-input {
          width: 100%; background: transparent; border: 1px solid transparent; border-radius: 3px;
          font: inherit; color: inherit; padding: 3px 4px; box-sizing: border-box;
        }
        .pt-cell-input:hover { border-color: #d8d2c4; background: #faf9f5 !important; }
        .pt-cell-input:focus { outline: none; border-color: #8b7355; background: #fffdf6 !important; box-shadow: 0 0 0 2px rgba(139,115,85,0.15); }
        .pt-cell-select {
          width: 100%; background: transparent; border: 1px solid transparent; border-radius: 3px;
          font: inherit; padding: 3px 2px; cursor: pointer; box-sizing: border-box;
        }
        .pt-cell-select:hover { border-color: #d8d2c4; background: #faf9f5 !important; }
        .pt-cell-select:focus { outline: none; border-color: #8b7355; background: #fffdf6 !important; }
        .pt-cell-select option { background: #fff; color: #1a1a1a; }
        .pt-cc-select { min-width: 110px; white-space: nowrap; }
        .pt-toolbtn { border: 1px solid #d8d2c4; background: #fff; border-radius: 5px; padding: 6px 11px; cursor: pointer; display: inline-flex; align-items: center; gap: 5px; color: #6b5745; font-size: 12px; }
        .pt-toolbtn:hover { background: #f7f5ef; }
      `}</style>
      <div data-testid="pt-sheet" style={{ background: '#fff', color: '#1a1a1a', width: '100%', maxWidth: 1680, height: '95vh', borderRadius: 8, boxShadow: '0 20px 80px rgba(0,0,0,0.7)', display: 'flex', flexDirection: 'column', fontFamily: '-apple-system, "Segoe UI", Roboto, Arial, sans-serif', fontSize: 13 }}>

        {/* Header */}
        <div data-testid="pt-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', borderBottom: '2px solid #8b7355', padding: '16px 20px 10px', flexShrink: 0 }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 24, letterSpacing: '.04em', color: '#6b5745', fontWeight: 700 }}>PURCHASE TRACKER</h1>
            <div style={{ color: '#8a8a8a', fontSize: 12 }}>{subtitle}</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{ display: 'flex', gap: 18, alignItems: 'center' }} data-testid="purchase-tracker-totals">
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 9.5, color: '#8a8a8a', textTransform: 'uppercase', letterSpacing: '.05em' }}>Outstanding</div>
                <div style={{ fontSize: 20, fontWeight: 800, color: '#8A3A3A' }} data-testid="pt-outstanding">{money(outstanding) || '$0.00'}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 9.5, color: '#8a8a8a', textTransform: 'uppercase', letterSpacing: '.05em' }}>Fulfilled</div>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#2E6B50' }} data-testid="pt-fulfilled">{money(fulfilledTotal) || '$0.00'}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 9.5, color: '#8a8a8a', textTransform: 'uppercase', letterSpacing: '.05em' }}>Full Order</div>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#6b5745' }} data-testid="pt-fullorder">{money(fullOrder) || '$0.00'}</div>
              </div>
            </div>
            <span style={{ fontSize: 11, minWidth: 68, textAlign: 'right', color: saveState === 'error' ? '#8A3A3A' : '#2E6B50' }} data-testid="pt-save-state">
              {saveState === 'saving' ? 'Saving…' : saveState === 'saved' ? 'Saved ✓' : saveState === 'error' ? 'Save failed' : ''}
            </span>
            <button onClick={() => setShowCards(true)} title="Manage credit cards" data-testid="purchase-tracker-cards-btn" className="pt-toolbtn">
              <CreditCard size={13} /> Cards
            </button>
            <button onClick={load} title="Refresh from FF&E" data-testid="purchase-tracker-refresh-btn" className="pt-toolbtn">
              <RefreshCw size={13} /> Refresh
            </button>
            <button onClick={openPrint} data-testid="purchase-tracker-print-btn"
              style={{ border: 'none', background: '#6b5745', color: '#fff', borderRadius: 5, padding: '7px 14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 700 }}>
              <Printer size={13} /> Print
            </button>
            <button onClick={onClose} data-testid="purchase-tracker-close-btn" title="Close"
              style={{ border: '1px solid #d8d2c4', background: '#fff', borderRadius: 5, padding: '6px 7px', cursor: 'pointer', color: '#6b5745', display: 'flex' }}>
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Toolbar: per-card totals + sort control */}
        <div data-testid="pt-toolbar" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, padding: '9px 20px', borderBottom: '1px solid #ece9e1', background: '#faf9f5', flexShrink: 0, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }} data-testid="pt-per-card">
            <span style={{ fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: '#6b5745' }}>Total per card</span>
            {(perCard || []).length === 0 ? <span style={{ color: '#aaa', fontSize: 12 }}>—</span> : (perCard || []).map(([c, v]) => (
              <span key={c} data-testid={`pt-card-total-${c}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#fff', border: '1px solid #d8d2c4', borderRadius: 999, padding: '3px 10px', fontSize: 12 }}>
                {c} <b style={{ color: '#6b5745' }}>{money(v) || '$0.00'}</b>
              </span>
            ))}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <ArrowUpDown size={14} color="#6b5745" />
            <span style={{ fontSize: 12, color: '#6b5745', fontWeight: 600 }}>Sort by</span>
            <select value={sortKey} onChange={(e) => setSortKey(e.target.value)} data-testid="pt-sort-select"
              style={{ border: '1px solid #d8d2c4', borderRadius: 5, padding: '5px 8px', fontSize: 12, color: '#1a1a1a', background: '#fff' }}>
              {COLUMNS.map((c) => <option key={c.sort} value={c.sort}>{c.label}</option>)}
            </select>
            <button onClick={() => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))} data-testid="pt-sort-dir" className="pt-toolbtn" title="Toggle direction">
              {sortDir === 'asc' ? <ArrowUp size={13} /> : <ArrowDown size={13} />} {sortDir === 'asc' ? 'Asc' : 'Desc'}
            </button>
          </div>
        </div>

        {/* Sheet body */}
        <div data-testid="pt-body" style={{ overflow: 'auto', flex: 1, padding: '0 20px 20px' }}>
          {rows === null ? (
            <div data-testid="pt-loading" style={{ padding: 40, textAlign: 'center', color: '#999' }}>Loading purchase tracker…</div>
          ) : rows.length === 0 ? (
            <div data-testid="pt-empty" style={{ padding: 40, textAlign: 'center', color: '#999' }}>Nothing on this sheet yet.</div>
          ) : (
            <table style={{ width: '100%', minWidth: 1650, borderCollapse: 'collapse' }} data-testid="purchase-tracker-table">
              <thead>
                <tr>
                  <th style={{ ...th, width: '1%', textAlign: 'center', cursor: 'default' }} title="Tag to task list">Tag</th>
                  {COLUMNS.map((c) => <SortHead key={c.key} col={c} />)}
                </tr>
              </thead>
              <tbody>
                {(sortedRows || []).map((r, idx) => {
                  const fulfilled = isFulfilled(r);
                  const line = goods(r);
                  const isOrderShip = !!(r.order_number || '').trim();
                  const prevRow = idx > 0 ? sortedRows[idx - 1] : null;
                  const newOrderGroup = sortKey === 'order_number' && idx > 0 &&
                    (((prevRow && prevRow.order_number) || '').trim() !== (r.order_number || '').trim());
                  return (
                    <React.Fragment key={r.item_id}>
                      {newOrderGroup ? (
                        <tr aria-hidden="true"><td colSpan={17} style={{ padding: 0, height: 0, borderTop: '2px solid #cbb89a' }}></td></tr>
                      ) : null}
                    <tr data-testid={`pt-row-${r.item_id}`}
                      style={r.tagged ? { background: '#fff6e0', boxShadow: 'inset 3px 0 0 #E0A82E' } : {}}>
                      <td style={{ ...td, textAlign: 'center' }}>
                        <button onClick={() => toggleTag(r)} data-testid={`pt-tag-${r.item_id}`}
                          title={r.tagged ? 'Tagged — click to remove from task list' : 'Tag to task list'}
                          style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: 2, color: r.tagged ? '#E0A82E' : '#c9c4b8', display: 'inline-flex' }}>
                          <Tag size={16} fill={r.tagged ? '#E0A82E' : 'none'} />
                        </button>
                      </td>
                      <td style={td}>
                        {(r.image_url || r.finish_image || (r.photos || []).length) ? (
                          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 5 }} data-testid={`pt-thumbs-${r.item_id}`}>
                            {r.image_url ? <img src={r.image_url} alt="item" title="Item photo" style={PT_THUMB} /> : null}
                            {r.finish_image && r.finish_image !== r.image_url ? <img src={r.finish_image} alt="finish" title="Finish swatch" style={{ ...PT_THUMB, border: '2px solid #8b6b2e' }} /> : null}
                            {(r.photos || []).filter((p) => p && p !== r.image_url && p !== r.finish_image).slice(0, 3).map((p, i) => <img key={i} src={p} alt="" style={PT_THUMB} />)}
                          </div>
                        ) : null}
                        <Cell testId={`pt-name-${r.item_id}`} value={r.name} onSave={(v) => saveField(r.item_id, 'name', v)} style={{ fontWeight: 500 }} />
                        {r.subcategory_name ? <div style={{ color: '#8a8a8a', fontSize: 11, paddingLeft: 4 }}>{r.subcategory_name}</div> : null}
                        <button onClick={() => setPhotoRow(r)} data-testid={`pt-photos-${r.item_id}`}
                          title={(r.photos || []).length ? `${(r.photos || []).length} photo(s)` : 'Add photos'}
                          style={{ marginTop: 3, marginLeft: 2, border: '1px solid #d8d2c4', background: (r.photos || []).length ? '#f0ede4' : '#fff', borderRadius: 5, padding: '2px 7px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#6b5745' }}>
                          <Camera size={12} /> {(r.photos || []).length ? (r.photos || []).length : 'Add'}
                        </button>
                      </td>
                      <td style={{ ...td, color: '#8a8a8a', whiteSpace: 'nowrap' }}>{r.room_name}</td>
                      <td style={td}>
                        <Cell testId={`pt-vendor-${r.item_id}`} value={r.vendor} onSave={(v) => saveField(r.item_id, 'vendor', v)} />
                        <Cell testId={`pt-sku-${r.item_id}`} value={r.sku} onSave={(v) => saveField(r.item_id, 'sku', v)} placeholder="SKU" style={{ color: '#8a8a8a', fontSize: 11 }} />
                      </td>
                      <td style={{ ...td, whiteSpace: 'nowrap', width: '1%' }}>
                        <select className="pt-cell-select pt-cc-select" value={r.credit_card || ''}
                          data-testid={`pt-cc-${r.item_id}`}
                          onChange={(e) => saveField(r.item_id, 'credit_card', e.target.value)}>
                          <option value=""></option>
                          {cards.map((c) => <option key={c} value={c}>{c}</option>)}
                          {r.credit_card && !cards.includes(r.credit_card) ? <option value={r.credit_card}>{r.credit_card}</option> : null}
                        </select>
                      </td>
                      <td style={td}>
                        <Cell testId={`pt-qty-${r.item_id}`} type="number" value={r.quantity} onSave={(v) => saveField(r.item_id, 'quantity', parseInt(v, 10) || 1)} style={{ textAlign: 'right' }} />
                      </td>
                      <td style={td}>
                        <Cell testId={`pt-cost-${r.item_id}`} type="number" value={r.cost || ''} onSave={(v) => saveField(r.item_id, 'cost', parseFloat(v) || 0)} style={{ textAlign: 'right' }} placeholder="0.00" />
                      </td>
                      <td style={{ ...td, textAlign: 'right' }}>
                        <Cell testId={`pt-ship-${r.item_id}`} type="number" value={shipDisplay(r)}
                          onSave={(v) => (isOrderShip ? saveOrderShipping(r.order_number.trim(), parseFloat(v) || 0) : saveField(r.item_id, 'shipping_cost', parseFloat(v) || 0))}
                          style={{ textAlign: 'right' }} placeholder="0.00" />
                        {isOrderShip ? <div style={{ fontSize: 8.5, color: '#a99', textAlign: 'right', paddingRight: 4 }}>per order</div> : null}
                      </td>
                      <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', paddingTop: 9 }}
                        title={fulfilled ? 'Fulfilled — subtracted from the outstanding balance' : ''}>
                        <span style={fulfilled ? { color: '#2E6B50', textDecoration: 'line-through' } : {}}>{money(line)}</span>
                        {fulfilled && line ? <span style={{ color: '#2E6B50', fontSize: 10, marginLeft: 3 }}>−</span> : null}
                      </td>
                      <td style={td}>
                        <Cell testId={`pt-order-${r.item_id}`} value={r.order_number} onSave={(v) => saveField(r.item_id, 'order_number', v)} placeholder="Order #" />
                      </td>
                      <td style={td}>
                        <Cell testId={`pt-ordered-${r.item_id}`} type="date" value={(r.order_date || '').slice(0, 10)} onSave={(v) => saveField(r.item_id, 'order_date', v)} />
                      </td>
                      <td style={td}>
                        <Cell testId={`pt-shipped-${r.item_id}`} type="date" value={(r.ship_date || '').slice(0, 10)} onSave={(v) => saveField(r.item_id, 'ship_date', v)} />
                      </td>
                      <td style={td}>
                        <Cell testId={`pt-expected-${r.item_id}`} type="date" value={(r.expected_delivery || '').slice(0, 10)} onSave={(v) => saveField(r.item_id, 'expected_delivery', v)} />
                      </td>
                      <td style={td}>
                        <Cell testId={`pt-delivered-${r.item_id}`} type="date" value={(r.actual_delivery || '').slice(0, 10)} onSave={(v) => saveField(r.item_id, 'actual_delivery', v)} />
                      </td>
                      <td style={td}>
                        <select className="pt-cell-select" value={r.carrier || ''} data-testid={`pt-carrier-${r.item_id}`} onChange={(e) => saveField(r.item_id, 'carrier', e.target.value)}>
                          <option value=""></option>
                          {carriers.map((c) => <option key={c} value={c}>{c}</option>)}
                          {r.carrier && !carriers.includes(r.carrier) ? <option value={r.carrier}>{r.carrier}</option> : null}
                        </select>
                        <Cell testId={`pt-tracking-${r.item_id}`} value={r.tracking_number} onSave={(v) => saveField(r.item_id, 'tracking_number', v)} placeholder="Tracking #"
                          style={{ fontFamily: 'ui-monospace, Menlo, Consolas, monospace', fontSize: 11, color: '#555' }} />
                      </td>
                      <td style={td}>
                        <select className="pt-cell-select" value={r.ship_to || ''} data-testid={`pt-shipto-${r.item_id}`} onChange={(e) => saveField(r.item_id, 'ship_to', e.target.value)}>
                          {SHIP_TO_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                          {r.ship_to && !SHIP_TO_OPTIONS.includes(r.ship_to) ? <option value={r.ship_to}>{r.ship_to}</option> : null}
                        </select>
                      </td>
                      <td style={td}>
                        <select className="pt-cell-select" value={r.status || ''} data-testid={`pt-status-${r.item_id}`} onChange={(e) => saveField(r.item_id, 'status', e.target.value)}
                          style={{ color: statusColor(r.status), fontWeight: 600 }}>
                          {STATUS_OPTIONS.map((o) => <option key={o} value={o}>{titleCase(o)}</option>)}
                          {r.status && !STATUS_OPTIONS.includes(r.status) ? <option value={r.status}>{titleCase(r.status)}</option> : null}
                        </select>
                      </td>
                    </tr>
                    </React.Fragment>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={8} style={{ borderTop: '1px solid #d8d2c4', fontWeight: 600, paddingTop: 7, textAlign: 'right', color: '#6b5745' }}>FULL ORDER</td>
                  <td style={{ borderTop: '1px solid #d8d2c4', fontWeight: 600, paddingTop: 7, textAlign: 'right', whiteSpace: 'nowrap', color: '#6b5745' }}>{money(fullOrder) || '$0.00'}</td>
                  <td colSpan={8} style={{ borderTop: '1px solid #d8d2c4' }}></td>
                </tr>
                <tr>
                  <td colSpan={8} style={{ fontWeight: 600, textAlign: 'right', color: '#2E6B50' }}>FULFILLED (shipped / delivered)</td>
                  <td style={{ fontWeight: 600, textAlign: 'right', whiteSpace: 'nowrap', color: '#2E6B50', textDecoration: 'line-through' }}>{money(fulfilledTotal) || '$0.00'}</td>
                  <td colSpan={8}></td>
                </tr>
                <tr>
                  <td colSpan={8} style={{ borderTop: '2px solid #8b7355', fontWeight: 800, paddingTop: 8, textAlign: 'right', fontSize: 14 }}>OUTSTANDING BALANCE</td>
                  <td style={{ borderTop: '2px solid #8b7355', fontWeight: 800, paddingTop: 8, textAlign: 'right', whiteSpace: 'nowrap', color: '#8A3A3A', fontSize: 14 }}>{money(outstanding) || '$0.00'}</td>
                  <td colSpan={8} style={{ borderTop: '2px solid #8b7355' }}></td>
                </tr>
              </tfoot>
            </table>
          )}
        </div>

        {/* Credit-card manager */}
        {showCards && (
          <div data-testid="pt-card-manager" style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 8 }}
            onClick={() => setShowCards(false)}>
            <div data-testid="pt-card-manager-panel" onClick={(e) => e.stopPropagation()} style={{ background: '#fff', width: 360, borderRadius: 8, boxShadow: '0 12px 48px rgba(0,0,0,0.4)', padding: 18 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <div style={{ fontWeight: 700, color: '#6b5745', fontSize: 15, display: 'flex', alignItems: 'center', gap: 6 }}><CreditCard size={16} /> Credit Cards</div>
                <button onClick={() => setShowCards(false)} data-testid="pt-card-manager-close" style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#6b5745' }}><X size={18} /></button>
              </div>
              <div style={{ fontSize: 12, color: '#8a8a8a', marginBottom: 12 }}>Add or remove the cards you charge purchases to. Numbers change — edit them here any time.</div>
              <div style={{ maxHeight: 240, overflowY: 'auto', marginBottom: 12 }}>
                {(cards || []).length === 0 ? (
                  <div style={{ color: '#aaa', fontSize: 13, padding: '8px 0' }}>No cards yet.</div>
                ) : (cards || []).map((c) => (
                  <div key={c} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 10px', border: '1px solid #eee', borderRadius: 6, marginBottom: 6 }}>
                    <span style={{ fontSize: 14 }}>{c}</span>
                    <button onClick={() => removeCard(c)} data-testid={`pt-card-remove-${c}`} title="Remove"
                      style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#8A3A3A' }}><X size={15} /></button>
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 7 }}>
                <input value={newCard} onChange={(e) => setNewCard(e.target.value)} placeholder="e.g. Amex …1234"
                  data-testid="pt-card-input"
                  onKeyDown={(e) => { if (e.key === 'Enter') addCard(); }}
                  style={{ flex: 1, border: '1px solid #d8d2c4', borderRadius: 6, padding: '8px 10px', fontSize: 14, color: '#1a1a1a' }} />
                <button onClick={addCard} data-testid="pt-card-add"
                  style={{ border: 'none', background: '#6b5745', color: '#fff', borderRadius: 6, padding: '8px 14px', cursor: 'pointer', fontWeight: 700, fontSize: 14 }}>Add</button>
              </div>
            </div>
          </div>
        )}
      </div>

      {photoRow && (
        <ItemPhotosModal
          item={{ id: photoRow.item_id, name: photoRow.name, photos: photoRow.photos || [] }}
          onClose={() => setPhotoRow(null)}
          onSaved={(next) => {
            setRows((prev) => (prev || []).map((x) => (x.item_id === photoRow.item_id ? { ...x, photos: next } : x)));
            setPhotoRow((cur) => (cur ? { ...cur, photos: next } : cur));
          }}
        />
      )}
    </div>,
    document.body
  );
};

export default PurchaseTrackerModal;
